import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { DateAdapter, MAT_DATE_FORMATS, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ActionItemDto, ActionItemStatus, ActionItemType, ActionItemWorkScope, ApprovalFlowDto, ApprovalState, ChangeActionitemStatusRequestDto, Client, GeneralTabDto, ReportType, ShutDownType, TimelineItemDto } from '@fdm/api-client';
import { ToastrService } from 'ngx-toastr';
import { catchError, combineLatest, debounceTime, distinctUntilChanged, filter, finalize, first, forkJoin, map, mergeMap, Observable, of, skip, switchMap, tap } from 'rxjs';
import { ApprovalFlowService, ReportFormPage, ReportFormStore } from '../../../reports/services';
import { LoaderService } from '../../../services/loader.service';
import { ConfirmDialogComponent, ConfirmDialogPayload, ConfirmDialogResponse, CreateNoteDialog, TimelineComponent, TimelineRecord, ToolbarComponent } from '../../../shared';
import { getErrorMessage, noWhitespaceValidator, utcToLocal } from '../../../utils';
import { ActionItemTypeOptions, ActionItemWorkScopeOptions, ApprovalFlowStateOptions } from '../../../utils/enum-maps';
import { AuthService, CommonUserService, EmailDescriptionService, Role } from '@fdm/services';
import { MatDividerModule } from '@angular/material/divider';
import { PolicyService } from '../../../reports/services/roles/policy.service';
import { MaterialModule } from '@fdm/material';
import moment from 'moment';
import { AppDateAdapter } from '../../../core/date/app-date-adapter';
import { APP_DATE_FORMATS } from '../../../core/date/app-date-formats';
import { MatDialog } from '@angular/material/dialog';
import { AngularEditorModule, AngularEditorConfig } from '@kolkov/angular-editor';
import { createEditorConfigs } from '../../../reports/components/report-form/shared/richtext-editor-config-helper';
import { ActionItemCreateApiService } from './action-item-create-api.service';
import { NoteAttachmentApiService } from '../../../reports/components/report-form/tabs/note-attachment-tab/note-attachment-api.service';

interface ActionItemForm {
  actionItemId: FormControl<string | null>;
  status: FormControl<ActionItemStatus | null>;
  actionItemDescriptionEn: FormControl<string | null>;
  actionItemDescriptionRu: FormControl<string | null>;
  actionItemDescriptionKz: FormControl<string | null>;
  actionItemOwner: FormControl<string | null>;
  ownerFullName: FormControl<string | null>;
  dueDate: FormControl<Date | null>;
  completedOn: FormControl<Date | null>;
  createdOn: FormControl<Date | null>;
  actionType: FormControl<ActionItemType | null>;
  workScope: FormControl<ActionItemWorkScope | null>;
  progress: FormControl<number | null>;
}
@Component({
  selector: 'app-action-item-create',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, RouterModule,
    MatFormFieldModule, MatSelectModule, MatInputModule, MatIconModule,
    MatButtonModule, MatDatepickerModule, MatNativeDateModule,
    MatAutocompleteModule,
    ToolbarComponent,
    MatSnackBarModule, MatCardModule,
    MatDividerModule,
    MaterialModule,
    AngularEditorModule,
    TimelineComponent
  ],
  templateUrl: './action-item-create.component.html',
  styleUrls: ['./action-item-create.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: DateAdapter, useClass: AppDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS },
    ActionItemCreateApiService,
    NoteAttachmentApiService
  ],
})
export class ActionItemCreateComponent implements OnInit, OnDestroy {
  private static readonly EMAIL_AUTOCOMPLETE_DEBOUNCE_MS = 1200;

  isDisabled = true;
  canEdit= false;
  private aiStatus = signal<ActionItemStatus | null>(ActionItemStatus.Draft);

  private editorConfigs = createEditorConfigs(3, 'action-item');
  configEN: AngularEditorConfig = this.editorConfigs[0];
  configRU: AngularEditorConfig = this.editorConfigs[1];
  configKZ: AngularEditorConfig = this.editorConfigs[2];

  private _router = inject(Router);
  private fb = inject(FormBuilder);
  private _route = inject(ActivatedRoute);
  private _destroyRef = inject(DestroyRef);
  private _apiClient = inject(Client);
  private _api = inject(ActionItemCreateApiService);
  private _loader = inject(LoaderService);
  private _toastrService = inject(ToastrService);
  private _cdr = inject(ChangeDetectorRef);
  private _approvalFlowService = inject(ApprovalFlowService);
  private store = inject(ReportFormStore);
  private _common_scripts = inject(CommonUserService); //common scripts
  private _policy = inject(PolicyService);
  private _dialog = inject(MatDialog);
  private _auth = inject(AuthService);
  private emailDescriptionService = inject(EmailDescriptionService);
  private _noteApi = inject(NoteAttachmentApiService);

  public roles$ = this._auth.roles$;
  public isCompleteActive = false;

  loadingFields = signal<{ [key: string]: boolean }>({});

  // Generic autocomplete options for email fields
  emailAutocompleteOptions = signal<{ [key: string]: string[] }>({});

  workScopeArray = signal<ActionItemWorkScope[]>([]);
  uniqueId = signal<string | undefined>(undefined);
  reportTitleEn = signal<string | undefined>(undefined);
  reportStatus = signal<string | undefined>(undefined);
  resolutionTargetDate = signal<Date | undefined>(undefined);
  ownerEmail = signal<string | undefined>(undefined);
  saveCounter = 0;

  private generalTabId: string | null = null;
  private resolutionAndMitigationPlanId: string | null | number = '';
  actionItemId = signal<number | undefined>(undefined)

  public actionTypeOptions = (ActionItemTypeOptions)
  public workScopeOptions = (ActionItemWorkScopeOptions)

  form: FormGroup<ActionItemForm>;

  isEditing = toSignal(this._route.paramMap.pipe(map(params => params.has('id'))));
  title = toSignal(this._route.paramMap.pipe(map(() => this.isEditing() ? `Edit Action Item` : 'New Action Item')));

  private reportType = toSignal(this.store.reportType$);
  private generalReport = toSignal(this.store.general$);
  private roles = toSignal(this._auth.roles$, { initialValue: [] as Role[] });

  isOwnerValid = computed(() => {
    const owner = this.generalReport()?.owner;
    return owner ? this._common_scripts.matchesWithField(owner) : false;
  });

  isInitiatorValid = computed(() => {
    // Recompute when user email aliases are loaded/updated.
    this.userEmails();
    const initiator = this.generalReport()?.initiator;
    return initiator ? this._common_scripts.matchesWithField(initiator) : false;
  });
  private userEmails = toSignal(this._common_scripts.userEmails$, { initialValue: [] as string[] });

  reportOwner = computed(() => this.generalReport()?.owner);

  dueDatePickerMax = computed(() => {
    const aiID = this.actionItemId();
    const tDate = this.resolutionTargetDate();
    return aiID ? null : tDate;
  })

  private get actionItemOwner() {
    return this.form.get('actionItemOwner') as FormControl<string | null>;
  }
  private get workScope() {
    return this.form.get('workScope') as FormControl<ActionItemWorkScope | null>;
  }

  public get ownerFullName() {
    return this.form.get('ownerFullName') as FormControl<string | null>;
  }

  public readonlyFields: string[] = [
    'actionItemId',
    'createdOn',
    'completedOn'
  ];

  progressStatus = signal<string>('VALID');

  enableComplete = computed(() => {
    const reportType = this.reportType();
    const aiStatus = this.form.get('status')?.value;
    const actionItemId = this.actionItemId();
    const generalReport = this.generalReport();
    const ownerEmail = this.ownerEmail();
    const state = generalReport?.currentState;
    // const isEmailMatches = ownerEmail?.toLowerCase().trim() === generalReport?.initiator?.toLowerCase().trim(); //this is a potential issue
    const isEmailMatches = ownerEmail ? this._common_scripts.matchesWithField(ownerEmail) : false;

    // if (isEmailMatches && actionItemId && reportType === ReportType.AnomalyReport && state === ApprovalState.PlanApproved) {
    if (isEmailMatches && actionItemId && state === ApprovalState.PlanApproved && aiStatus === ActionItemStatus.Approved) {
      return true;
    }

    return false;
  });

  enableChangeDueDate = computed(() => {
    const generalReport = this.generalReport();
    const aiStatus = this.form.get('status')?.value;
    const state = generalReport?.currentState;

    return (
      aiStatus === ActionItemStatus.Approved &&
      (
        state === ApprovalState.PlanApproved ||
        state === ApprovalState.ResolutionPlanCompleted ||
        state === ApprovalState.ValidationAndVerificationCompleted
      ) &&
      this.isOwnerValid()
    );
  });

  enableReActivate = computed(() => {
    const generalReport = this.generalReport();
    const aiStatus = this.aiStatus();
    const actionItemId = this.actionItemId();
    const state = generalReport?.currentState;

    const canReactivateByState =
      state === ApprovalState.PlanApproved ||
      state === ApprovalState.ResolutionPlanCompleted;

    const canReactivateByRole =
      this.isInitiatorValid() ||
      this.roles().includes(Role.Admin);

    return (
      !!actionItemId &&
      aiStatus === ActionItemStatus.Completed &&
      canReactivateByState &&
      canReactivateByRole
    );
  });

  timelineList: TimelineRecord[] = [];

  constructor() {
    this.store.changePage(ReportFormPage.ACTION_ITEM);

    this.form = this.fb.group<ActionItemForm>({
      actionItemId: new FormControl({ value: '', disabled: true }),
      status: new FormControl({ value: ActionItemStatus.Draft, disabled: true }),
      actionItemDescriptionEn: new FormControl('', [Validators.required, Validators.maxLength(20000), noWhitespaceValidator()]),
      actionItemDescriptionRu: new FormControl('', [Validators.maxLength(20000)]),
      actionItemDescriptionKz: new FormControl('', [Validators.maxLength(20000)]),
      actionItemOwner: new FormControl(null, [Validators.required, Validators.maxLength(255)]),
      ownerFullName: new FormControl(null, [Validators.maxLength(2048)]),
      dueDate: new FormControl(null, [Validators.required]),
      createdOn: new FormControl(null, [Validators.required]),
      completedOn: new FormControl(null, [Validators.required]),
      actionType: new FormControl(null, [Validators.required]),
      workScope: new FormControl(null, [Validators.required]),
      progress: new FormControl<number | null>(null, [Validators.min(0), Validators.max(100)]),
    }, {
      validators: this.dueDateValidator.bind(this)
    });
    this.form.get('createdOn')?.disable();
    this.form.get('completedOn')?.disable();
  }


  richTextFieldPolicy() {
    this.configEN.editable = this.isFieldEditable('actionItemDescriptionEn');
    this.configRU.editable = this.isFieldEditable('actionItemDescriptionRu');
    this.configKZ.editable = this.isFieldEditable('actionItemDescriptionKz');
  }

  shutdownChangeonGeneralTab() {
    this._apiClient.byGeneralTab4(this.generalTabId ?? "").subscribe((data) => {
      this.workScopeArray.set(
        (data?.actionItems ?? [])
          .filter(item => item.id?.toString() !== this.actionItemId())
          .map(item => item.workScope)
      );

      const workScopeValue = this.form.get('workScope')?.value;
      if (workScopeValue) {
        this.workScopeArray.set([...this.workScopeArray(), workScopeValue]);
      }

      const requiredShutdownValue = this.workScopeArray().includes(ActionItemWorkScope.TA)
        ? ShutDownType.Yes
        : ShutDownType.No;

      const currentShutdownValue = this.generalReport()?.shutDownRequired;

      if (currentShutdownValue === requiredShutdownValue) {
        return;
      }

      const generalReport = this.generalReport();

      if (!generalReport) {
        return;
      }

      const retirementDateMoment = moment(generalReport.retirementDate);
      const payload = GeneralTabDto.fromJS({
        ...generalReport,
        retirementDate: retirementDateMoment.isValid() && retirementDateMoment.year() < 2000 ? undefined : generalReport.retirementDate,
        shutDownRequired: requiredShutdownValue
      });

      this._apiClient.generalTabPUT(this.generalTabId ?? "", payload).subscribe();
    });


  }

  private dueDateValidator(formGroup: AbstractControl): ValidationErrors | null {
    const dueDateControl = formGroup.get('dueDate');
    const dueDate = dueDateControl?.value;
    const targetDate = this.resolutionTargetDate();

    if (dueDateControl && dueDate && targetDate) {
      const dDate = new Date(dueDate);
      dDate.setHours(0, 0, 0, 0);
      const tDate = new Date(targetDate);
      tDate.setHours(0, 0, 0, 0);

      if (dDate > tDate) {
        dueDateControl.setErrors({ ...dueDateControl.errors, dueDateGreaterThanTarget: true });
      } else {
        const { dueDateGreaterThanTarget, ...errors } = dueDateControl.errors || {};
        if (dueDateGreaterThanTarget) {
          dueDateControl.setErrors(Object.keys(errors).length > 0 ? errors : null);
        }
      }
    }
    return null;
  }

  ngOnInit(): void {

    this.loadActionItem();

    this.form.get('status')?.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((status) => {
        this.aiStatus.set(status ?? null);
      });

    this.aiStatus.set(this.form.get('status')?.value ?? null);

    this.form.get('progress')!.statusChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(status => this.progressStatus.set(status));

    combineLatest([this.store.general$, this.roles$])
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(([general, roles]) => {
        const state = general?.currentState;
        this.isCompleteActive = roles.includes(Role.Admin) && (state === ApprovalState.PlanApproved);
      });

    this.actionItemOwner.valueChanges
      .pipe(
        debounceTime(ActionItemCreateComponent.EMAIL_AUTOCOMPLETE_DEBOUNCE_MS),
        distinctUntilChanged(),
        tap(() => {
          this.loadingFields.update(fields => ({ ...fields, ['actionItemOwner']: true }));
        }),
        switchMap(owner => {
          if (owner == null || owner.trim() === '') {
            this.form.get('ownerFullName')?.setValue('');
            // Clear autocomplete options when input is empty
            this.emailAutocompleteOptions.update(options => ({
              ...options,
              actionItemOwner: []
            }));
            this._cdr.markForCheck();
            return of([]).pipe(
              finalize(() => {
                this.loadingFields.update(fields => ({ ...fields, ['actionItemOwner']: false }));
              })
            );
          }

          return this._apiClient.email(owner).pipe(
            catchError(err => {
              console.warn('Error fetching email suggestions for actionItemOwner:', err);
              this.emailAutocompleteOptions.update(options => ({
                ...options,
                actionItemOwner: []
              }));
              return of([]);
            }),
            finalize(() => {
              this.loadingFields.update(fields => ({ ...fields, ['actionItemOwner']: false }));
            })
          );
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(owners => {
        if (owners && owners.length > 0) {
          const resolvedEmail = this.emailDescriptionService.getPreferredEmailForApprovalFlow(owners);
          if (resolvedEmail && this.actionItemOwner.dirty) {
            this.actionItemOwner.setValue(resolvedEmail, { emitEvent: false });
          }

          const searchText = this.actionItemOwner.value?.trim().toLowerCase() || '';
          const emailOptions = owners.flatMap(item => [
            item.email,
            item.preferredEmail,
            item.sharedEmail
          ])
            .filter((emailValue): emailValue is string =>
              !!emailValue && emailValue.trim() !== ''
            )
            .filter(emailValue =>
              emailValue.toLowerCase().includes(searchText)
            );

          const distinctEmailOptions = [...new Set(
            emailOptions.map(e => e.toLowerCase())
          )];

          this.emailAutocompleteOptions.update(options => ({
            ...options,
            actionItemOwner: distinctEmailOptions
          }));
        } else {
          this.emailAutocompleteOptions.update(options => ({
            ...options,
            actionItemOwner: []
          }));
        }
        this._cdr.markForCheck();
      });

    this.workScope.valueChanges
      .pipe(
        this.isEditing() ? skip(1) : takeUntilDestroyed(this._destroyRef),
        filter(value => value === ActionItemWorkScope.TA || value === ActionItemWorkScope.NonTA),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => {
        this.shutdownChangeonGeneralTab();
      });

    combineLatest([this._policy.canEdit$, this._policy.canEditAIOwner$])
      .pipe(
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(([canEdit, canEditAIOwner]) => {
        this.canEdit = canEdit;

        if (canEdit && this.form) {
          const controls = (this.form as FormGroup).controls;
          Object.keys(controls).forEach((key) => {
            if (this.readonlyFields.includes(key)) {
              controls[key].disable({ emitEvent: false });
            } else {
              controls[key].enable({ emitEvent: false });
            }
          });
        } else {
          this.form?.disable({ emitEvent: false });
        }

        // For Anomaly Report: allow changing AI Owner until Plan Approved
        // This override applies after general form enable/disable logic
        // Only apply on EDIT (existing action item). On CREATE, let canEdit$
        // control the field so the Initiator (and others with create rights
        // per INITIATOR_POLICY) can assign the initial AI Owner.
        const isEditing = !!this.actionItemId();
        if (isEditing) {
          if (canEditAIOwner && this.actionItemOwner) {
            this.actionItemOwner.enable({ emitEvent: false });
          } else if (this.actionItemOwner && !canEditAIOwner) {
            this.actionItemOwner.disable({ emitEvent: false });
          }
        }

        // Progress follows generic edit permissions, but is available only in Plan Approved stage.
        const progressControl = this.form.get('progress');
        const isPlanApproved = this.generalReport()?.currentState === ApprovalState.PlanApproved;
        if (progressControl) {
          if (canEdit && isPlanApproved) {
            progressControl.enable({ emitEvent: false });
          } else {
            progressControl.disable({ emitEvent: false });
          }
        }

        this.richTextFieldPolicy();
      });
  }

  isFieldEditable(fieldName: string): boolean {
    const control = this.form?.get(fieldName);
    return !!(this.canEdit && control && !control.disabled);
  }

  onProgressKeyPress(event: KeyboardEvent): boolean {
    const charCode = event.charCode;
    // Allow only digits 0-9
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  ngOnDestroy(): void {
    this._common_scripts.isAIOwner.set(false);
  }

  loadActionItem() {
    this._route.paramMap.pipe(
      switchMap(params => {
        const id = params.get('id');
        if (!id) {
          const queryParams = this._route.snapshot.queryParamMap;
          this.generalTabId = queryParams.get('generalTabId');
          this.resolutionAndMitigationPlanId = queryParams.get('resolutionAndMitigationPlanId');
          return of(null);
        }

        this.actionItemId.set(+id);

        this._loader.startProcessing();

        return forkJoin({
          actionItems: this._getAIbyId(+id),
          // timeline: this._getAITimelinebyId(+id),
          // notes: this._getAINotesbyId(+id),
        });

        // return this._apiClient.actionItemGET(+id).pipe(
        //   finalize(() => this._loader.stopLoading()),
        //   catchError(err => {
        //     if (err.status === 404) {
        //       return of(null);
        //     }
        //     console.error(err);
        //     this._toastrService.error('Failed to load Action Item.');
        //     return of(null);
        //   })
        // );
      }),
      switchMap((res) => {
        const actionItemData = res?.actionItems;

        this._loader.stopProcessing()

        if (actionItemData) {
          this.form.patchValue(actionItemData);
          this.aiStatus.set(actionItemData.status ?? null);
          this.ownerEmail.set(actionItemData.actionItemOwner);
          this.generalTabId = actionItemData.generalTabId ?? null;
          this.resolutionAndMitigationPlanId = actionItemData.resolutionAndMitigationPlanId ?? null;

          if (!this.form.get('ownerFullName')?.value?.trim()) {
            this.populateOwnerFullNameFallback(actionItemData.actionItemOwner);
          }

          const isMatched = this._common_scripts.matchesWithField(actionItemData.actionItemOwner);
          this._common_scripts.isAIOwner.set(isMatched);

          if (actionItemData.status == ActionItemStatus.Completed || actionItemData.status == ActionItemStatus.Approved) {
            // For Anomaly Report, allow policy to control actionItemOwner editability (may be edited until Plan Approved)
            // For other report types, disable all fields
            // This will be refined by policy subscription that may enable actionItemOwner separately
            this.form.disable();
            this.form.updateValueAndValidity();
          }
        }

        if (this.generalTabId) {
          this._loader.startLoading();
          return this._apiClient.generalTabGET2(this.generalTabId).pipe(
            finalize(() => this._loader.stopLoading()),
            catchError(err => {
              console.error(err);
              this._toastrService.error('Failed to load General Tab details.');
              return of(null);
            })
          );
        }

        return of(null);
      }),
      takeUntilDestroyed(this._destroyRef)
    ).subscribe(generalTabData => {
      if (generalTabData) {
        const resolutionTargetDate = moment(generalTabData.resolutionTargetDate);
        const retirementDate = moment(generalTabData.retirementDate);

        const payload = GeneralTabDto.fromJS({
          ...generalTabData,
          resolutionTargetDate: resolutionTargetDate.isValid() && resolutionTargetDate.year() > 1970
            ? utcToLocal(resolutionTargetDate.toDate())
            : null,
          retirementDate: retirementDate.isValid() && retirementDate.year() > 1970
            ? utcToLocal(retirementDate.toDate())
            : null,
        });

        this.store.setGeneral(payload); //Do we need it?
        this.uniqueId.set(payload.uniqueId);
        this.reportTitleEn.set(payload.reportTitleEn);
        this.resolutionTargetDate.set(payload.resolutionTargetDate);
        this.reportStatus.set(ApprovalFlowStateOptions.find(option => option.value === payload.currentState)?.label || '');

        this.form.get('dueDate')?.updateValueAndValidity();
        this._cdr.markForCheck();
      }
      if (this.generalTabId) {
        this.loadApprovalFlowDetails();
      }
    });
  }

  getErrorMessage(fieldName: keyof ActionItemForm): string | null {
    const control = this.form.get(fieldName);
    const aiStatus = this.form.get('status')?.value;
    if (aiStatus === ActionItemStatus.Approved) {
      if (control?.hasError('dueDateGreaterThanTarget')) {
        return 'New due date is later than resolution target date.';
      }
    } else {
      if (control?.hasError('dueDateGreaterThanTarget')) {
        return 'You cannot select due date greater than resolution target date.';
      }
    }
    return getErrorMessage(this.form, fieldName, this.displayNames);
  }

  getEmailAutocompleteOptions(fieldName: string): string[] {
    return this.emailAutocompleteOptions()[fieldName] || [];
  }

  onEmailAutocompleteSelected(event: MatAutocompleteSelectedEvent, emailFieldName: string, descriptionFieldName: string): void {
    const selectedEmail = event.option.value;
    const emailControl = this.form.get(emailFieldName);
    const descriptionControl = this.form.get(descriptionFieldName);

    if (!selectedEmail || !emailControl || !descriptionControl) {
      return;
    }

    this.loadingFields.update(fields => ({ ...fields, [emailFieldName]: true }));

    this._apiClient.email(selectedEmail.trim()).pipe(
      tap((response) => {
        if (response && response.length > 0) {
          // Use emailDescriptionService to process descriptions
          const descriptions = this.emailDescriptionService.processOwnerDescriptions(response);
          descriptionControl.setValue(descriptions);

          descriptionControl.markAsDirty();
          this.form.markAsDirty();
        } else {
          descriptionControl.setValue(null);
        }
        this.loadingFields.update(fields => ({ ...fields, [emailFieldName]: false }));
        this._cdr.markForCheck();
      }),
      catchError(err => {
        console.warn(`Error fetching email details for ${emailFieldName}:`, err);
        this.loadingFields.update(fields => ({ ...fields, [emailFieldName]: false }));
        return of(null);
      }),
      takeUntilDestroyed(this._destroyRef)
    ).subscribe();
  }

  protected displayNames: { [key: string]: string } = {
    actionItemId: 'Action Item ID',
    actionItemDescriptionEn: 'Description (EN)',
    actionItemDescriptionRu: 'Description (RU)',
    actionItemDescriptionKz: 'Description (KZ)',
    reportOwner: 'Report owner',
    actionItemOwner: 'Assigned to/Action item owner',
    dueDate: 'Due Date',
    actionType: 'Action Type',
    workScope: 'Work Scope',
    status: 'Status',
    createdOn: 'Created On',
    completedOn: 'Completed On',
    progress: 'Action Item Progress Completion'
  };

  save(): void {
    if (this.saveCounter > 0) {
      return;
    }
    this.saveCounter++;
    this.handleSave(false);
  }

  saveAndClose(): void {
    if (this.saveCounter > 0) {
      return;
    }
    this.saveCounter++;
    this.handleSave(true);
  }

  private handleSave(close: boolean): void {
    if (this.form.invalid) {
      this._toastrService.error('Please fill all required fields.', 'Close', { timeOut: 3000 });
      this.saveCounter = 0;
      return;
    }
    this._loader.startProcessing();

    const dueDateLocal = this.form.get('dueDate')?.value;
    const adjustedDueDate = dueDateLocal ? new Date(dueDateLocal) : null;
    if (adjustedDueDate) {
      adjustedDueDate.setHours(adjustedDueDate.getHours() + 5);
    }

    const formValue = this.form.getRawValue();
    const actionItem = ActionItemDto.fromJS({
      ...formValue,
      id: this.actionItemId() ?? undefined,
      deviationReportID: this.uniqueId() ?? undefined,
      generalTabId: this.generalTabId,
      dueDate: adjustedDueDate ?? undefined,
      status: this.isEditing() ? formValue.status ?? undefined : ActionItemStatus.Draft,
      resolutionAndMitigationPlanId: this.resolutionAndMitigationPlanId
    });

    const request$: Observable<number | undefined> = this.isEditing()
      ? this._apiClient.actionItemPUT(actionItem).pipe(map(() => undefined))
      : this._apiClient.actionItemPOST(actionItem).pipe(map(res => res ?? undefined));

    request$.pipe(
      first(),
      finalize(() => {
        this.saveCounter = 0;
        this._loader.stopProcessing();
      })
    ).subscribe({
      next: (res: number | undefined) => {
        if (!this.actionItemId() && res) {
          this.actionItemId.set(res);
        }
        this.form.markAsPristine();
        this._toastrService.success('Action Item saved successfully.');
        if (close) {
          this.navigateToReport();
        } else if (!this.isEditing()) {
          this._router.navigate(['reports/action-items/edit', this.actionItemId()], {
            queryParamsHandling: 'preserve'
          });
        }
      },
      error: (error: unknown) => {
        console.error(error);
        this._toastrService.error('Failed to save Action Item.');
      }
    });
  }

  navigateToReport(): void {
    const navigateTo = ['/reports', this.generalTabId];
    this._router.navigate(navigateTo, { queryParams: { step: ReportFormPage.MITIGATION_AND_RESOLUTION_PLAN } });
  }

  confirmAIcomplete(): void {
    const dialog = this._dialog.open<ConfirmDialogComponent, ConfirmDialogPayload, ConfirmDialogResponse | undefined>(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Action Item Complete',
        message: 'Do you want to complete Action Item?',
        comment: '',
        commentRequired: true
      },
      width: '500px',
    });

    dialog.afterClosed().pipe(first()).subscribe((res) => {
      if (res?.isConfirmed && res?.comment) {
        // complete the action item
        this.completeAI(res.comment);
        // Create note in main project first
        this.createNoteForCompletedAI(res.comment);

      }
    });
  }

  completeAI(comment: string) {
    const id = this.actionItemId();

    if (id == undefined) {
      return;
    }
    this._loader.startLoading();

    const body = ChangeActionitemStatusRequestDto.fromJS({
      status: ActionItemStatus.Completed,
      comment
    });

    this._approvalFlowService.completeAI(+id, body)
      .pipe(
        catchError(err => {
          console.warn(err);
          this._loader.stopLoading();
          return of(err);
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => {
        this._toastrService.success(`You've successfully completed the action "${ActionItemStatus.Completed}"`);

        this._loader.stopLoading();

        this.navigateToReport();
      });
  }

  private createNoteForCompletedAI(comment: string): void {
    if (!this.generalTabId) {
      console.warn('No generalTabId available to create note');
      return;
    }

    const actionItemId = this.form.get('actionItemId')?.value || 'AI-' + this.actionItemId();
    const noteTitle = `Action Item Completed: ${actionItemId}`;
    const noteText = comment;

    this._noteApi.createNote(this.generalTabId, {
      title: noteTitle,
      text: noteText,
      files: []
    }).pipe(
      first(),
      catchError(err => {
        console.warn('Failed to create note in main project:', err);
        this._toastrService.warning('Action Item completed, but note creation failed');
        return of(null);
      })
    ).subscribe(() => {
      console.log('Note created successfully in main project');
    });
  }

  private loadApprovalFlowDetails() {
    if (!this.generalTabId) {
      return;
    }

    this._apiClient.byGeneralTab(this.generalTabId).pipe(
      catchError(err => {
        console.error(err);
        this._loader.stopLoading();
        return of(null);
      })
    ).subscribe(data => {
      if (data) {
        this.store.setApprovalFlow(ApprovalFlowDto.fromJS({ ...data, generalTabId: this.generalTabId as string }));
      }
    });
  }

  private _getAIbyId(id: number) {
    // this._loader.startLoading();

    return this._apiClient.actionItemGET(id).pipe(
      // finalize(() => this._loader.stopLoading()),
      catchError(err => {
        if (err.status === 404) {
          return of(null);
        }
        console.error(err);
        this._toastrService.error('Failed to load Action Item.');
        return of(null);
      })
    );
  }


  private populateOwnerFullNameFallback(ownerEmail: string | null | undefined): void {
    const currentValue = this.form.get('ownerFullName')?.value?.trim();
    const normalizedEmail = ownerEmail?.trim();

    if (currentValue || !normalizedEmail) {
      return;
    }

    this._apiClient.email(normalizedEmail).pipe(
      tap((owners) => {
        const ownerFullName = this.emailDescriptionService.processOwnerDescriptions(owners);
        if (ownerFullName && !this.form.get('ownerFullName')?.value?.trim()) {
          this.form.get('ownerFullName')?.setValue(ownerFullName);
          this._cdr.markForCheck();
        }
      }),
      catchError(err => {
        console.warn('Error fetching owner full name fallback:', err);
        return of(null);
      }),
      takeUntilDestroyed(this._destroyRef)
    ).subscribe();
  }
  // private _getAITimelinebyId(id: number) {

  //   return this._apiClient.timeline(id).pipe(
  //     tap(res => {
  //       this.timelineList = res.map(item => {
  //         const author = item.author?.split(',');

  //         return {
  //           ...item,
  //           name: author ? author[0][0] + author[1].trim()[0] : null,
  //         } as TimelineRecord
  //       });
  //     }),
  //     catchError(err => {
  //       if (err.status === 404) {
  //         return of(null);
  //       }
  //       console.error(err);
  //       this._toastrService.error('Failed to load Action Item timeline.');
  //       return of(null);
  //     })
  //   );
  // }

  changeDueDate(): void {
    this.form.get('dueDate')?.enable();
  }

  reactivateAI(comment: string) {
    const id = this.actionItemId();

    if (id == undefined) {
      return;
    }
    this._loader.startLoading();

    const body = ChangeActionitemStatusRequestDto.fromJS({
      status: ActionItemStatus.Approved,
      comment
    });

    this._approvalFlowService.reActivateAI(+id, body)
      .pipe(
        catchError(err => {
          console.warn(err);
          this._loader.stopLoading();
          return of(err);
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => {
        this._toastrService.success(`You've successfully re-activated the action "${ActionItemStatus.Approved}"`);

        this._loader.stopLoading();

        this.navigateToReport();
      });
  }

  reActivateActionItem(): void {
    const dialog = this._dialog.open<ConfirmDialogComponent, ConfirmDialogPayload, ConfirmDialogResponse | undefined>(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Action Item Re-Activation',
        message: 'Do you want to re-activate Action Item?',
        comment: '',
        commentRequired: true
      },
      width: '500px',
    });

    dialog.afterClosed().pipe(first()).subscribe((res) => {
      if (res?.isConfirmed && res?.comment) {
        this.reactivateAI(res.comment);
      }
    });
    /*
Pop-up window for comment should appear and required to filled in.
   If Report's stage was "Plan approved", then stage is not changing
   If Report's stage was "Resolution plan completed" then it should be changed to "Plan Approved".
   AI status should be changed from "Closed" to "Approved".

System should send notification to AI owner: "Action required! Your action item {ID} was re-activated,
please proceed with closure according to the Comment. Comment: {comment}. Link {link}" TBA! Will update by full notification text

FDM events should show 2 records:
For Reports changes:
State = Plan Approved
Action item status = empty
Comment = empty
Type = {Report type} status changed
Created ON
Created By
For AI changes:
State = Approved
Action item status = Approved
Comment = comment text
Type = "AI re-activated" (is it possible)? If it is hard to do, then can leave as "AI status change"
Created ON
Created By
    */
  }

  clearDate(key: string) {
    this.form.get(key)?.setValue(null)
  }

  createNote() {
    const aiId: number | undefined = this.actionItemId() ?? undefined;

    if (!aiId) {
      return;
    }

    this._dialog
      .open(CreateNoteDialog)
      .afterClosed()
      .pipe(
        first(),
        mergeMap((res) => {
          if (res && aiId) {
            this._loader.startProcessing();

            return this._api.createNote(aiId, res)
            // .pipe(
            //   mergeMap(() => this._getAITimelinebyId(aiId))
            // );
          }

          return of(null);
        }),
        catchError((err) => {
          this._loader.stopProcessing();
          return of(err);
        }),
        finalize(() => {
          this._cdr.markForCheck();
          this._loader.stopProcessing();
        })
      )
      .subscribe();
  }

  download(blobName?: string) {
    console.log(blobName);

    if (!blobName) {
      return;
    }

    const noteId = this.timelineList.find(item => item.attachments?.some(file => file.blobName === blobName))?.noteId;

    console.log('noteid', noteId);

    if (noteId && blobName) {
      this._api.download(blobName, noteId).subscribe();
    }
  }
}
